import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import {
  Loader2, ArrowLeft, Send, MessageSquareText, Dumbbell, Ruler,
  TrendingUp, RefreshCw,
} from 'lucide-react';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Input } from './ui/input';
import {
  adminClientProgress, adminClientMessages, adminClientMessageSend,
} from '../services/api';

const fmtFull = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('hu-HU', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' }); } catch { return iso; }
};

const VERDICT = {
  'fejlődés': 'bg-emerald-500/15 text-emerald-300', 'plató': 'bg-amber-500/15 text-amber-300',
  'visszaesés': 'bg-sky-500/15 text-sky-300', 'gyűjtés': 'bg-gray-600/30 text-gray-400',
  'fogyás': 'bg-emerald-500/15 text-emerald-300', 'hízás': 'bg-amber-500/15 text-amber-300',
  'csökkenés': 'bg-emerald-500/15 text-emerald-300', 'növekedés': 'bg-amber-500/15 text-amber-300',
};
const Chip = ({ v }) => (
  <span className={`text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded ${VERDICT[v] || VERDICT['gyűjtés']}`}>{v}</span>
);

const LogRow = ({ log, comment, onComment }) => {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const send = async () => {
    if (text.trim().length < 2) return;
    setBusy(true);
    try { await onComment(log.id, text.trim()); setText(''); setOpen(false); }
    finally { setBusy(false); }
  };
  const setsStr = log.sets?.length
    ? log.sets.map((s) => `${s.kg}kg×${s.reps} (RPE ${10 - s.rir})`).join(' · ')
    : [log.weight_kg != null && `${log.weight_kg} kg`, log.reps != null && `× ${log.reps}`, log.sets_done != null && `× ${log.sets_done} sor.`].filter(Boolean).join(' ');
  return (
    <div data-testid={`admin-log-${log.id}`} className="rounded-lg border border-gray-800 bg-gray-900/40 px-3 py-2.5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="min-w-0">
          <span className="text-sm font-semibold text-white">{log.exercise_name}</span>
          <span className="text-xs text-gray-400 ml-2">{setsStr}</span>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className="text-[11px] text-gray-500">{fmtFull(log.created_at)}{log.logged_by === 'coach' ? ' · coach' : ''}</span>
          <Button data-testid={`admin-log-comment-btn-${log.id}`} variant="ghost" size="sm" onClick={() => setOpen((v) => !v)}
            className="h-7 text-xs text-gray-400 hover:text-yellow-400">
            <MessageSquareText size={12} className="mr-1" /> Komment
          </Button>
        </div>
      </div>
      {comment && (
        <div className="mt-2 rounded-md bg-yellow-500/10 border border-yellow-700/30 px-3 py-2 text-xs text-yellow-200/90">
          <span className="font-bold text-yellow-300">Gábor:</span> {comment.message}
        </div>
      )}
      {open && (
        <div className="mt-2 flex gap-2">
          <Input data-testid={`admin-log-comment-input-${log.id}`} value={text} onChange={(e) => setText(e.target.value)}
            placeholder="pl. Szép munka! Jövő héten +2,5 kg mehet." className="h-9 bg-gray-950 border-gray-700 text-white text-sm" />
          <Button data-testid={`admin-log-comment-send-${log.id}`} size="sm" onClick={send} disabled={busy || text.trim().length < 2}
            className="bg-yellow-600 hover:bg-yellow-700 text-gray-900 h-9 shrink-0">
            {busy ? <Loader2 className="animate-spin" size={13} /> : <Send size={13} />}
          </Button>
        </div>
      )}
    </div>
  );
};

export const AdminClientProgress = ({ adminKey, user, onBack }) => {
  const [data, setData] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checkin, setCheckin] = useState('');
  const [sending, setSending] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, m] = await Promise.all([
        adminClientProgress(adminKey, user.user_id),
        adminClientMessages(adminKey, user.user_id),
      ]);
      setData(p);
      setMessages(m.messages || []);
    } catch (_) { toast.error('Betöltési hiba.'); }
    finally { setLoading(false); }
  }, [adminKey, user.user_id]);

  useEffect(() => { load(); }, [load]);

  const sendMessage = async (message, logId = null) => {
    try {
      const res = await adminClientMessageSend(adminKey, user.user_id, { message, log_id: logId });
      toast.success(res.email_status === 'sent' ? 'Elküldve — a kliens emailt is kapott.' : 'Elküldve.');
      const m = await adminClientMessages(adminKey, user.user_id);
      setMessages(m.messages || []);
    } catch (_) { toast.error('A küldés nem sikerült.'); throw new Error(); }
  };

  const sendCheckin = async () => {
    if (checkin.trim().length < 2) return;
    setSending(true);
    try { await sendMessage(checkin.trim()); setCheckin(''); }
    catch (_) { /* toast already shown */ }
    finally { setSending(false); }
  };

  const commentByLog = {};
  messages.filter((m) => m.log_id && m.sender === 'coach').forEach((m) => { commentByLog[m.log_id] = m; });
  const thread = messages.filter((m) => !m.log_id);
  const fb = data?.feedback;

  return (
    <div data-testid="admin-client-progress" className="min-h-screen bg-gray-950 text-white">
      <header className="border-b border-gray-800 bg-gray-900/80 backdrop-blur sticky top-0 z-10">
        <div className="container mx-auto px-6 py-4 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <button data-testid="admin-progress-back" onClick={onBack} className="text-gray-400 hover:text-white"><ArrowLeft size={18} /></button>
            <div>
              <h1 className="text-lg font-bold" style={{ fontFamily: 'Montserrat, sans-serif' }}>{user.name || user.email} — haladás</h1>
              <p className="text-xs text-gray-400">{user.email} · {user.level || 'kezdő'}{user.split?.type ? ` · ${user.split.type} ${user.split.sessionsPerWeek || ''}×/hét` : ''}</p>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={load} className="text-gray-400 hover:text-white"><RefreshCw size={15} className={loading ? 'animate-spin' : ''} /></Button>
        </div>
      </header>

      <main className="container mx-auto px-6 py-8 max-w-4xl space-y-6">
        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="animate-spin text-yellow-500" size={28} /></div>
        ) : (
          <>
            {/* Verdiktek */}
            {fb && (
              <section data-testid="admin-progress-verdicts" className="rounded-2xl border border-gray-800 bg-gray-900/60 p-5">
                <h2 className="text-xs font-bold uppercase tracking-widest text-yellow-500 mb-3 flex items-center gap-2">
                  <TrendingUp size={13} /> Visszajelzés-motor
                </h2>
                <p className="text-sm text-gray-300 mb-3">{fb.headline}</p>
                <div className="space-y-1.5 text-sm">
                  {(fb.strength || []).filter((s) => s.series.length).map((s) => (
                    <div key={s.exercise} className="flex flex-wrap items-center gap-2">
                      <Dumbbell size={12} className="text-gray-500" />
                      <span className="text-gray-200">{s.exercise}</span>
                      <Chip v={s.verdict} />
                      {s.change_pct != null && <span className="text-xs text-gray-500">{s.change_pct > 0 ? '+' : ''}{s.change_pct}%</span>}
                      <span className="text-xs text-gray-500">({s.series.length} hét)</span>
                    </div>
                  ))}
                  {fb.weight?.series?.length > 0 && (
                    <div className="flex items-center gap-2"><Ruler size={12} className="text-gray-500" /><span className="text-gray-200">Testsúly</span><Chip v={fb.weight.verdict} /></div>
                  )}
                  {fb.waist?.series?.length > 0 && (
                    <div className="flex items-center gap-2"><Ruler size={12} className="text-gray-500" /><span className="text-gray-200">Derék</span><Chip v={fb.waist.verdict} /></div>
                  )}
                </div>
              </section>
            )}

            {/* Check-in üzenetek */}
            <section data-testid="admin-checkin-section" className="rounded-2xl border border-gray-800 bg-gray-900/60 p-5">
              <h2 className="text-xs font-bold uppercase tracking-widest text-yellow-500 mb-3 flex items-center gap-2">
                <MessageSquareText size={13} /> Heti check-in — üzenetek
              </h2>
              <div className="space-y-2 max-h-72 overflow-y-auto mb-3">
                {thread.length === 0 && <p className="text-sm text-gray-500">Még nincs üzenetváltás.</p>}
                {thread.map((m) => (
                  <div key={m.id} data-testid={`checkin-msg-${m.id}`}
                    className={`rounded-lg px-3 py-2 text-sm max-w-[85%] ${m.sender === 'coach'
                      ? 'bg-yellow-500/10 border border-yellow-700/30 text-yellow-100 ml-auto'
                      : 'bg-gray-800 text-gray-200'}`}>
                    <div className="text-[10px] uppercase tracking-widest mb-0.5 opacity-60">
                      {m.sender === 'coach' ? 'Te (Gábor)' : (user.name || 'Kliens')} · {fmtFull(m.created_at)}
                    </div>
                    <div className="whitespace-pre-wrap">{m.message}</div>
                  </div>
                ))}
              </div>
              <div className="flex gap-2">
                <Textarea data-testid="admin-checkin-input" value={checkin} onChange={(e) => setCheckin(e.target.value)} rows={2}
                  placeholder="pl. Szuper hét volt! A guggolásnál jövő héten emelünk. A derékméret szépen megy lefelé — így tovább!"
                  className="bg-gray-950 border-gray-700 text-white text-sm" />
                <Button data-testid="admin-checkin-send" onClick={sendCheckin} disabled={sending || checkin.trim().length < 2}
                  className="bg-yellow-600 hover:bg-yellow-700 text-gray-900 font-bold shrink-0 self-end">
                  {sending ? <Loader2 className="animate-spin" size={14} /> : <><Send size={14} className="mr-1.5" /> Küldés</>}
                </Button>
              </div>
              <p className="text-[11px] text-gray-600 mt-2">A kliens emailt kap az üzenetről, és a fiókjában tud válaszolni.</p>
            </section>

            {/* Edzésnaplók */}
            <section data-testid="admin-logs-section">
              <h2 className="text-xs font-bold uppercase tracking-widest text-gray-500 mb-2">Edzésnaplók ({(data?.workout_logs || []).length})</h2>
              <div className="space-y-2">
                {(data?.workout_logs || []).slice(0, 30).map((l) => (
                  <LogRow key={l.id} log={l} comment={commentByLog[l.id]}
                    onComment={(logId, text) => sendMessage(text, logId)} />
                ))}
                {(data?.workout_logs || []).length === 0 && <p className="text-sm text-gray-500">Még nincs naplóbejegyzés.</p>}
              </div>
            </section>

            {/* Mérések */}
            <section data-testid="admin-measurements-section">
              <h2 className="text-xs font-bold uppercase tracking-widest text-gray-500 mb-2">Testméretek ({(data?.measurements || []).length})</h2>
              <div className="space-y-1.5">
                {(data?.measurements || []).slice(-10).reverse().map((m) => (
                  <div key={m.id} className="flex flex-wrap items-center justify-between text-sm rounded-lg border border-gray-800 bg-gray-900/40 px-3 py-2">
                    <span className="text-gray-300">
                      {m.weight_kg != null && `${m.weight_kg} kg`}{m.waist_cm != null && ` · derék ${m.waist_cm} cm`}
                      {m.neck_cm != null && ` · nyak ${m.neck_cm} cm`}{m.hip_cm != null && ` · csípő ${m.hip_cm} cm`}
                    </span>
                    <span className="text-[11px] text-gray-500">{fmtFull(m.created_at)}{m.logged_by === 'coach' ? ' · coach' : ''}</span>
                  </div>
                ))}
              </div>
            </section>
          </>
        )}
      </main>
    </div>
  );
};

export default AdminClientProgress;
